/**
 * The 12 platform roles across 4 portals. Authorization is **capability-based** (see
 * `constants/permissions.ts` + `AuthStore.hasPermission`) — never branch on role identity in
 * features (ADR-015).
 *
 * The string values are the `custom:role` claim and MUST match the backend's role codes exactly.
 *
 * **`NAM` and `NAM_READER` are gone** (vantage-docs#80, ruled 2026-08-10): *"Corporate Writer is a
 * role. National Account Manager is a TYPE OF USER that is a corporate writer."* An account manager
 * is an assignment — `user_account_access` with `role = 'ACCOUNT_MANAGER'` — and that assignment
 * drives what their dashboard shows, never what they may touch. The seeded role table is 12 rows.
 */
export enum Role {
  // ── Corporate portal (internal) ──────────────────────────────────────────
  SuperAdmin = 'SUPER_ADMIN',
  /** Replaces the legacy CORPORATE_ADMIN. */
  CorporateWriter = 'CORPORATE_WRITER',

  // ── Territory (OIP) portal (internal) ────────────────────────────────────
  TerritoryAdmin = 'TERRITORY_ADMIN',
  TerritoryManager = 'TERRITORY_MANAGER',
  /** Replaces the legacy SALES/MANAGER/ADMIN/MEMBER write tier. */
  TerritoryWriter = 'TERRITORY_WRITER',
  TerritoryReader = 'TERRITORY_READER',
  /** OIP identity granted cross-portal access to act for unadopted subs. */
  SubProxy = 'SUB_PROXY',

  // ── Sub portal (external) ────────────────────────────────────────────────
  /** The sub company's own user manager. */
  SubAdmin = 'SUB_ADMIN',
  SubWriter = 'SUB_WRITER',
  SubReader = 'SUB_READER',

  // ── Client portal (external) ─────────────────────────────────────────────
  ClientWriter = 'CLIENT_WRITER',
  ClientReader = 'CLIENT_READER',
}

/** The 12 `Role` string values, built once from the enum itself so it can never drift from it. */
const ROLE_VALUES: ReadonlySet<string> = new Set<string>(Object.values(Role));

/**
 * Membership test for the `custom:role` claim against {@link Role} — the enumerator.
 *
 * Nothing in the auth path used to read this enum as a *validator*: `sessionFromIdToken` took the
 * claim as an opaque string and `AuthStore.role` cast it (`customRole() as Role`), so an
 * unrecognised value became a `Role` by assertion, drew an empty capability set from
 * `permissionsForRole` (unknown key → `new Set([])`), matched none of
 * `isOipUser`/`isCorpUser`/`isSubUser`/`isClientUser`, and was bounced to `/auth/login` by
 * `roleLandingGuard` with nothing said anywhere. The claim is minted by the backend and these
 * values mirror the seeded role table, so the mismatch is not reachable today — it becomes
 * reachable the next time a role is renamed, which is exactly how `NAM`/`NAM_READER` went
 * (vantage-docs#80).
 *
 * Use it wherever a role STRING crosses into the app from outside (the token, an API payload),
 * never to branch authorization — that is `AuthStore.hasPermission` (ADR-015).
 */
export function isRole(value: string): value is Role {
  return ROLE_VALUES.has(value);
}
